import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/categories", label: "Categories" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="sticky top-0 z-40 glass border-b border-white/5" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="h-7 w-7 rounded-sm bg-gradient-to-br from-blue-600 to-blue-500 grid place-items-center ring-gold">
              <span className="font-display font-bold text-white text-sm">V</span>
            </div>
            <div className="leading-none">
              <div className="font-display font-bold tracking-tight text-[15px]">VYR Trade</div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 mt-[2px]">INDIA → UK · EUROPE · GLOBAL</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 text-[13px] rounded-md transition-colors ${
                    isActive ? "text-white bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                  }`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/quote"
              className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white transition-colors"
              data-testid="cta-request-quote-header"
            >
              Request Quote <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <button
              className="lg:hidden p-2 text-zinc-300 hover:text-white"
              onClick={() => setOpen((o) => !o)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/5 bg-zinc-950/95" data-testid="mobile-menu">
            <div className="px-6 py-4 grid gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-2.5 text-sm rounded-md ${isActive ? "text-white bg-white/5" : "text-zinc-300 hover:bg-white/5"}`
                  }
                  data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/quote"
                className="mt-2 inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3.5 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="cta-request-quote-mobile"
              >
                Request Sourcing Quote
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <SiteFooter />
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-white/5 bg-zinc-950" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid gap-10 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-7 w-7 rounded-sm bg-gradient-to-br from-blue-600 to-blue-500 grid place-items-center">
              <span className="font-display font-bold text-white text-sm">V</span>
            </div>
            <div className="font-display font-bold tracking-tight">VYR Trade</div>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-xs">
            Connecting global buyers with trusted Indian suppliers.
          </p>
          <p className="mt-2 font-mono text-[10.5px] tracking-[0.22em] text-amber-300/90">
            DIRECT · TRANSPARENT · RELIABLE
          </p>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-3">EXPLORE</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/services" className="text-zinc-300 hover:text-white">Services</Link></li>
            <li><Link to="/categories" className="text-zinc-300 hover:text-white">Categories</Link></li>
            <li><Link to="/how-it-works" className="text-zinc-300 hover:text-white">How It Works</Link></li>
            <li><Link to="/insights" className="text-zinc-300 hover:text-white">Insights</Link></li>
            <li><Link to="/become-a-supplier" className="text-zinc-300 hover:text-white">Become a Supplier</Link></li>
            <li><Link to="/supplier-verification" className="text-zinc-300 hover:text-white">Supplier Verification</Link></li>
            <li><Link to="/about" className="text-zinc-300 hover:text-white">About</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-3">LEGAL</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="text-zinc-300 hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-zinc-300 hover:text-white">Terms</Link></li>
            <li><Link to="/admin/login" className="text-zinc-500 hover:text-zinc-300">Admin</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-3">CONTACT</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><a href="mailto:reachus@vyrtrade.com" className="hover:text-white">reachus@vyrtrade.com</a></li>
            <li className="font-mono text-[13px]">+44 7591 440397</li>
            <li className="text-zinc-400">London, UK · Hyderabad, India</li>
          </ul>
          <Link
            to="/quote"
            className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white"
            data-testid="cta-request-quote-footer"
          >
            Request Quote <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12px] text-zinc-500">
          <div>© {new Date().getFullYear()} VYR Trade. Global trade & sourcing facilitation — not a manufacturer unless explicitly stated.</div>
          <div className="font-mono tracking-[0.18em] text-zinc-600">vyrtrade.com</div>
        </div>
      </div>
    </footer>
  );
}
